import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import { default as enus } from '../../localization/en-us.json';
import { default as esus } from '../../localization/es-us.json';
import { default as frca } from '../../localization/fr-ca.json';
import { LocalizedStrings } from 'react-localization';
import { Context, Dispatch, ReactNode } from 'react';
import { LoanerOrRentalOption } from '../../API/Enums';


/**
 * An enumeration of the possible localizations.
 */
export enum Locales {
    /**
     * English locale.
     */
    en = 'en-us',
    /**
     * Spanish locale.
     */
    es = 'es-us',
    /**
     * French locale.
     */
    fr = 'fr-ca',
    /**
     * Default none locale, typically this will end up as the english localization.
     */
    none = ''
}

/**
 * A readonly object that maps the enumeration values as keys to their respecive languages.
 *
 * @note Since this acts more as an enumeration or type, it does not have an explicit type defined.
 */
export const LocalesFull = {
    [Locales.en]: 'English',
    [Locales.es]: 'Spanish',
    [Locales.fr]: 'French',
    [Locales.none]: 'None',
} as const;

/**
 * Since the LocaleMap object is used more like a type or enum, this type
 * exists soley to prevent a massive wall of text for the implied type when hovering
 * on the LocaleMap object.
 */
export type LocaleMapType = {
    [Locales.en]: typeof enus;
    [Locales.es]: typeof esus;
    [Locales.fr]: typeof frca;
    [Locales.none]: typeof enus;
};

/**
 * Map of all available locales to their corresponding import.
 *
 * @note This should be treated as readonly but is technically not readonly.
 */
export const LocaleMap: LocaleMapType = {
    [Locales.en]: enus,
    [Locales.es]: esus,
    [Locales.fr]: frca,
    [Locales.none]: enus,
};

/**
 * This type makes the English localization from the LocaleMap a required property.
 */
export type RequiredLocaleMap = Required<Pick<typeof LocaleMap, Locales.en>>;
/**
 * This type makes all localizations that are not English from the LocaleMap an optional property.
 */
export type PartialLocaleMap = Partial<Omit<typeof LocaleMap, Locales.en>>;

/**
 * Defines the underlying type of locale content where English is always required and all other locales are optional.
 */
export type LocaleContent = RequiredLocaleMap & PartialLocaleMap;

/**
 * This type is defined to cast the baseLocaleContent object as an object with only the English localization content
 * since the Typescript has problems with the way we create the optional and required properties on the LocaleContent type.
 */
export type BaseLocaleContentType = {
    [Locales.en]: typeof enus;
};

/**
 * Defines the type of the Localization object.
 */
export type LocalizationType = LocalizedStrings<typeof enus>;

/**
 * Defines the localization context state type.
 */
export type LocalizationContextState = {
    Localization: LocalizationType;
    locale: Locales;
};

/**
 * The function type for the enableLocaleReducer and disableLocaleReducer functions in the
 * localizationReducer enableLocale and disableLocale dispatches.
 */
export type LocaleReducerFunction = (previousContent: LocaleContent, locale: Locales) => LocaleContent;

/**
 * The types of dispatches that the localization context can accept.
 */
export enum LocalizationDispatchTypes {
    SetLocale = 'SET_LOCALE',
    Clear = 'CLEAR',
    EnableLocale = 'ENABLE_LOCALE',
    DisableLocale = 'DISABLE_LOCALE'
}

/**
 * The type definitions for each dispatch payload as related to the type of dispatch.
 */
export type LocalizationPayload = {
    [LocalizationDispatchTypes.SetLocale]: Locales | null;
    [LocalizationDispatchTypes.Clear]: never;
    [LocalizationDispatchTypes.EnableLocale]: Locales;
    [LocalizationDispatchTypes.DisableLocale]: Locales;
};

/**
 * Defines the object used as an argument for the dispatches in the localization context.
 */
export type LocalizationActions = ActionMap<LocalizationPayload>[keyof ActionMap<LocalizationPayload>];

/**
 * Defines the type for the localizationReducer function.
 */
export type LocalizationReducerFunction = (state: LocalizationContextState, actions: LocalizationActions[]) => LocalizationContextState;

/**
 * Defines the type for the state context.
 */
export type LocalizationState = LocalizationContextState | undefined;
/**
 * Defines the type for the dispatch context.
 */
export type LocalizationDispatch = Dispatch<LocalizationActions[]> | undefined;

/**
 * Defines the type for the created state context.
 */
export type LocalizationStateContextType = Context<LocalizationState>;
/**
 * Defines the type for the created dispatch context.
 */
export type LocalizationDispatchContextType = Context<LocalizationDispatch>;

/**
 * Defines the props type for the `LocalizationProvider`.
 */
export interface LocalizationProviderProps {
    /**
     * Any children nodes that will be rendered in side of the localization context.
     */
    children?: ReactNode;
}

/**
 * Defines the complete type for the `LocalizationProvider` component
 */
export type LocalizationProviderComponent = (props: LocalizationProviderProps) => ReactNode;

/**
 * Defines the return type for the `useLocale` hook.
 */
export type UseLocaleReturn = Locales;

/**
 * Defines the function type for the `useLocale` hook.
 */
export type UseLocaleHook = () => UseLocaleReturn;

/**
 * Defines the return type for the `useLocalization` hook.
 */
export type UseLocalizationReturn = LocalizationType;

/**
 * Defines the function type for the `useLocalization` hook.
 */
export type UseLocalizationHook = () => UseLocalizationReturn;

/**
 * Defines the return type for the `useLocalizationDispatch` hook.
 */
export type UseLocalizationDispatchReturn = (...args: LocalizationActions[]) => void;

/**
 * Defines the function type for the `useLocalizationDispatch` hook.
 */
export type UseLocalizationDispatchHook = () => UseLocalizationDispatchReturn;

/**
 * Defines the return type for the `useClearLocale` hook.
 */
export type UseClearLocaleHookReturn = {
    /**
     * Returned by the `useClearLocale` hook from the localization context. Clears the locale
     * if the current locale is not `en-us`.
     */
    clearLocale: VoidFunction;
};

/**
 * Defines the function type for the `useClearLocale` hook.
 */
export type UseClearLocaleHook = () => UseClearLocaleHookReturn;

/**
 * Defines the object map type for one of the possible type used on the `...values` argument in the `formatAsString` function.
 */
export type FormatAsStringObject = {
    [key: string]: string | number;
};

/**
 * Defines the function type for the `formatAsString` function returned by the `useLocalizationExtension` hook.
 */
export type FormatAsStringFunction = (str: string, ...values: Array<string | number | FormatAsStringObject>) => string;

/**
 * Defines the function type for the `formatRequired` function returned by the `useLocalizationExtension` hook.
 */
export type FormatRequiredFunction = (field: string) => string;

/**
 * Defines the function type for the `formatInvalid` function returned by the `useLocalizationExtension` hook.
 */
export type FormatInvalidFunction = (field: string) => string;

/**
 * Defines the return type for the `useLocalizationExtension` hook.
 */
export type UseLocalizationExtensionReturn = {
    /**
     * Format the passed string replacing the numbered placeholders.
     *  i.e. "I'd like some {0} and {1}", or just "{0}".
     *
     * @example strings.formatString(strings.question, strings.bread, strings.butter)
     */
    formatAsString: FormatAsStringFunction;
    /**
     * Returns a localized message stating the field is required.
     */
    formatRequired: FormatRequiredFunction;
    /**
     * Returns a localized message stating the field is invalid.
     */
    formatInvalid: FormatInvalidFunction;
    /**
     * The phone field invalid localized text.
     */
    phoneInvalid: LocalizationType['PhoneInvalid'];
    /**
     * The email field invalid localized text.
     */
    emailInvalid: LocalizationType['EmailInvalid'];
    /**
     * The zip code field invalid localized text.
     */
    zipCodeInvalid: LocalizationType['ZipCodeInvalid'];
    /**
     * The postal code field invalid localized text.
     */
    postalCodeInvalid: LocalizationType['PostalCodeCanadaError'];
    /**
     * The vin field invalid localized text.
     */
    vinInvalid: LocalizationType['VinInvalid'];
    /**
     * The vin field invalid localized text for either the full vin or the last 8 characters.
     */
    vinInvalidFullOrLast8: LocalizationType['VinInvalidFullOrLast8'];
    /**
     * The email or phone field invalid localized text.
     */
    emailOrPhoneInvalid: LocalizationType['EmailOrPhoneInvalid'];
};

/**
 * Defines the function type for the `useLocalizationExtension` hook.
 */
export type UseLocalizationExtensionHook = () => UseLocalizationExtensionReturn;

/**
 * Type definition for the useLoanerOrRentalLocalizationText hook props.
 */
export interface LoanerOrRentalLocalizationTextProps {
    /**
     * The current appointment's vehicle type that is being returned.
     */
    loanerOrRentalOption: LoanerOrRentalOption;
}

/**
 * Type definition for the useLoanerOrRentalLocalizationText hook return object.
 */
export interface LoanerOrRentalLocalizationTextReturn {
    /**
     * The formatted localized text to be used for header of the instructions
     * for how to exchange their key with a loaner or rental vehicle key.
     */
    headerText?: string;
    /**
     * The formatted and localized text to be used as the instruction text
     * for how to exchange their key with a loaner or rental vehicle key.
     */
    instructionText?: string;
}

/**
 * Defines the function type for the `useLoanerOrRentalLocalizationTextHook` hook.
 */
export type LoanerOrRentalLocalizationTextHook = GoMotoFunction<LoanerOrRentalLocalizationTextProps, LoanerOrRentalLocalizationTextReturn>;